/**
 * Read aloud, wherever a document is shown: the device's own voice (Web Speech API, no network), a visible
 * Stop while it reads, and — when this device has no voice — plain words saying the screen reader reads the
 * page instead (the button is not offered where it cannot work).
 */
import { useTranslation } from 'react-i18next';
import { useReadAloud } from '@/lib/docs';
import { useAnnounce } from './Announcer';
import { Button } from './ui/button';
import { Icon } from './Icons';

export function ReadAloud({ text, lang, testId = 'read-aloud' }: { text: string; lang: 'en' | 'es'; testId?: string }) {
  const { t } = useTranslation();
  const announce = useAnnounce();
  const speech = useReadAloud(lang);
  if (!speech.available) {
    return (
      <p className="help read-aloud-none" data-testid={`${testId}-none`}>
        <Icon name="speaker" /> {t('readAloud.none')}
      </p>
    );
  }
  return (
    <span className="read-aloud" data-testid={testId}>
      {speech.speaking ? (
        <Button
          variant="secondary"
          size="sm"
          onClick={() => {
            speech.stop();
            announce(t('readAloud.stopped'));
          }}
          data-testid={`${testId}-stop`}
        >
          <Icon name="x" /> {t('readAloud.stop')}
        </Button>
      ) : (
        <Button
          variant="secondary"
          size="sm"
          onClick={() => {
            if (!speech.start(text)) announce(t('readAloud.none'), 'assertive');
          }}
          data-testid={`${testId}-start`}
        >
          <Icon name="speaker" /> {t('readAloud.start')}
        </Button>
      )}
    </span>
  );
}
